import { useCallback, useState } from 'react';
import { api, ErreurApi } from './api';
import {
  lireCouchesWfs, lireCouchesWms, lireExceptionOgc, type CoucheAnnoncee,
} from '../../src/partage/lecture-ogc.js';

/**
 * SERVICES OGC — ce que Cadastral publie VERS UN SIG TIERS.
 *
 * ⚠️⚠️ CET ÉCRAN N'EST PAS UN CLIENT, ET C'EST DÉLIBÉRÉ. `/ogc/wfs` et
 * `/ogc/wms` existent pour QGIS, ArcGIS et GeoServer — pas pour nous. Dessiner
 * ici une carte WMS serait une SECONDE carte, plus lente, sur des données que
 * MapLibre sert déjà par tuiles vectorielles indexées. Ce dépôt a déjà payé
 * deux représentations d'un même objet (`carte.tsx` contre `assemblage.tsx`).
 *
 * Ce que cet écran fait, et que rien ne faisait : il rend le service
 * ADMINISTRABLE. Avant lui, rien dans l'application ne disait que Cadastral
 * parlait WFS et WMS. Une administration qui demandait « puis-je brancher mon
 * QGIS ? » n'avait aucune réponse dans le produit — alors que le service
 * tournait, éprouvé, en production.
 *
 * ⚠️ IL LIT LE DOCUMENT DU TIERS, PAS UNE ROUTE DE CONFORT. `GetCapabilities`
 * est exactement ce que QGIS demande en premier. Si cet écran liste une
 * couche, QGIS la verra ; s'il n'en liste aucune, QGIS n'en verra aucune. Une
 * route JSON taillée pour l'écran aurait divergé du vrai document, et on aurait
 * cru avoir fermé une couche qui restait servie.
 */

type Etat =
  | { phase: 'repos' }
  | { phase: 'interrogation' }
  | { phase: 'lu'; wfs: CoucheAnnoncee[]; wms: CoucheAnnoncee[] }
  | { phase: 'refus'; code: string; message: string }
  | { phase: 'panne'; message: string };

/** L'origine réelle du service — jamais écrite en dur. */
function origine(): string {
  return typeof window === 'undefined' ? '' : window.location.origin;
}

export function ServicesOgc() {
  const [etat, setEtat] = useState<Etat>({ phase: 'repos' });
  const [copie, setCopie] = useState<string | null>(null);

  const interroger = useCallback(async () => {
    setEtat({ phase: 'interrogation' });
    try {
      const [wfs, wms] = await Promise.all([api.ogc.capacitesWfs(), api.ogc.capacitesWms()]);
      /**
       * ⚠️⚠️ UN SERVICE OGC REFUSE EN 200. Il ne rend pas 403 : il rend un
       * `ServiceExceptionReport`, avec le code 200 et le type `text/xml`. Lire
       * le code HTTP seul présenterait un refus d'habilitation comme un
       * succès, et l'administrateur conclurait que le tiers voit tout.
       */
      const refus = lireExceptionOgc(wfs.contenu) ?? lireExceptionOgc(wms.contenu);
      if (refus) { setEtat({ phase: 'refus', ...refus }); return; }
      setEtat({
        phase: 'lu',
        wfs: lireCouchesWfs(wfs.contenu),
        wms: lireCouchesWms(wms.contenu),
      });
    } catch (e) {
      setEtat({
        phase: 'panne',
        message: e instanceof ErreurApi ? e.message : "Le service n'a pas répondu.",
      });
    }
  }, []);

  const copier = useCallback(async (texte: string, quoi: string) => {
    try {
      await navigator.clipboard.writeText(texte);
      setCopie(quoi);
    } catch {
      /* ⚠️ LE PRESSE-PAPIERS PEUT ÊTRE REFUSÉ (contexte non sécurisé, réglage
         du navigateur) et il échoue SANS BRUIT. Sans ce message, l'agent colle
         dans QGIS ce qu'il avait copié une heure plus tôt. */
      setCopie(`échec:${quoi}`);
    }
  }, []);

  const base = origine();
  const adresses: Array<{ cle: string; titre: string; url: string; version: string }> = [
    { cle: 'wfs', titre: 'WFS — les entités', url: `${base}/ogc/wfs`, version: '2.0.0' },
    { cle: 'wms', titre: 'WMS — les images', url: `${base}/ogc/wms`, version: '1.3.0' },
  ];

  return (
    <div className="ogc">
      <header className="ogc-tete">
        <h2>Services OGC</h2>
        <p>
          Cadastral publie son registre aux normes <strong>WFS 2.0.0</strong> et
          <strong> WMS 1.3.0</strong> : une administration branche son QGIS ou son ArcGIS
          sur ces deux adresses, sans qu'aucune donnée ne soit recopiée ni exportée.
        </p>
      </header>

      <section className="ogc-bloc">
        <h3>Les deux adresses</h3>
        {adresses.map((a) => (
          <div key={a.cle} className="ogc-adresse">
            <div>
              <span className="ogc-adresse-titre">{a.titre}</span>
              <code>{a.url}</code>
            </div>
            <div className="ogc-adresse-actions">
              <span className="ogc-version">version {a.version}</span>
              <button type="button" onClick={() => { void copier(a.url, a.cle); }}>
                {copie === a.cle ? 'Copiée' : copie === `échec:${a.cle}` ? 'Copie refusée' : 'Copier'}
              </button>
            </div>
          </div>
        ))}
        {copie?.startsWith('échec:') && (
          <p className="ogc-alerte" role="alert">
            Le navigateur a refusé l'accès au presse-papiers. Sélectionnez l'adresse
            et copiez-la à la main — ne collez pas ce que vous aviez copié avant.
          </p>
        )}
      </section>

      <section className="ogc-bloc">
        <h3>Ce que votre habilitation ouvre</h3>
        <p>
          Le service ne publie que les couches que votre rôle peut lire. Ce bouton demande
          le document <code>GetCapabilities</code> — celui que QGIS demande en premier.
        </p>
        <button type="button" className="ogc-interroger"
          onClick={() => { void interroger(); }}
          disabled={etat.phase === 'interrogation'}>
          {etat.phase === 'interrogation' ? 'Interrogation…' : 'Interroger le service'}
        </button>

        {etat.phase === 'panne' && <p className="ogc-alerte" role="alert">{etat.message}</p>}

        {etat.phase === 'refus' && (
          <p className="ogc-alerte" role="alert">
            Le service a refusé : <strong>{etat.code}</strong> — {etat.message}
          </p>
        )}

        {etat.phase === 'lu' && (
          <>
            <table className="ogc-couches">
              <thead>
                <tr><th>Couche</th><th>Titre</th><th>WFS</th><th>WMS</th></tr>
              </thead>
              <tbody>
                {Array.from(new Set([...etat.wfs, ...etat.wms].map((c) => c.nom))).map((nom) => {
                  const f = etat.wfs.find((c) => c.nom === nom);
                  const m = etat.wms.find((c) => c.nom === nom);
                  return (
                    <tr key={nom}>
                      <td><code>{nom}</code></td>
                      <td>{(f ?? m)?.titre ?? '—'}</td>
                      <td className="ogc-oui-non">{f ? 'oui' : 'non'}</td>
                      <td className="ogc-oui-non">{m ? 'oui' : 'non'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {etat.wfs.length === 0 && etat.wms.length === 0 && (
              <p className="ogc-alerte" role="alert">
                Aucune couche : votre rôle n'ouvre aucune des ressources publiées. Un client
                tiers configuré avec ce compte verrait un service vide.
              </p>
            )}
            {/**
              * ⚠️⚠️ ON NE PRÉSENTE PAS CE TABLEAU COMME UNE PREUVE DU FILTRE.
              * Mesuré le 24/09/2026 : les DOUZE rôles de `sec.role_permission`
              * lisent `cad.parcelle` ET `cad.section` — aucun ne les sépare.
              * « Vous voyez les deux couches » est donc, à ce jour,
              * indiscernable de « le filtre ne filtre rien ». Les deux se
              * ressemblent trait pour trait, et une seule est vraie.
              */}
            {etat.wfs.length === 2 && (
              <p className="ogc-reserve">
                Ce compte voit les deux couches. À ce jour <strong>aucun rôle ne sépare</strong>
                {' '}<code>cad.parcelle</code> de <code>cad.section</code> : ce résultat ne
                démontre donc pas que le filtrage par habilitation mord — il montre seulement
                ce que le service publie pour vous.
              </p>
            )}
          </>
        )}
      </section>

      <section className="ogc-bloc">
        <h3>Raccorder un client</h3>
        <p>
          Le service exige une habilitation Mowibo ID. Un client tiers doit présenter
          l'en-tête <code>Authorization: Bearer &lt;jeton&gt;</code> — dans QGIS :
          <em> Connexion WFS → Authentification → Configurations → En-tête HTTP</em>.
        </p>
        {/**
          * ⚠️ LE COOKIE DE SESSION NE VOYAGE PAS. Cet écran interroge le service
          * avec la session du navigateur ; QGIS ne l'a pas. Taire ce point
          * ferait conclure, d'un essai réussi ici, que la connexion marchera
          * là-bas — puis chercher la panne dans QGIS.
          */}
        <p className="ogc-reserve">
          Le bouton ci-dessus emprunte <strong>votre session de navigateur</strong>. QGIS ne
          l'a pas : un essai réussi ici ne dit rien de la connexion du tiers.
        </p>
        <p className="ogc-reserve">
          ⚠️ <strong>Aucun écran de l'atelier ne délivre ce jeton aujourd'hui.</strong> Il est
          émis par Mowibo ID ; Cadastral l'accepte, il ne le fabrique pas. Remettre un accès à
          une administration passe donc encore par l'exploitation.
        </p>
      </section>
    </div>
  );
}
